// Apply sequence for "Apply & Verify" (PCD-9663). See enrollmentRuns.spec.md ENR-FR-001.
// Mirrors EditServerRolesModal checkServicesHealth/submitForm: same calls, same bodies, same
// order. Returns a map of hostId -> error message for hosts whose host-config or role calls
// failed; an empty map means every call was accepted by resmgr.
import ApiClient from 'api-client/ApiClient'
import {
  assignDns,
  assignHostConfig,
  assignHypervisorRole,
  assignImageLibraryRole,
  assignPersistentStorageRole,
  unassignHostConfig,
  updateRemoteSupportRole,
} from 'openstack/components/infrastructure/actions'
import { trackEvent } from 'utils/tracking'
import type { EnrollmentSelection } from './model'

export const HEALTH_ERROR =
  "Compute and networking services aren't ready yet. Wait a few minutes and try again."

// Thrown when nova/neutron health check fails before any call is made.
export class ServicesNotReadyError extends Error {
  constructor() {
    super(HEALTH_ERROR)
    this.name = 'ServicesNotReadyError'
  }
}

// Minimal host shape the role actions need: an id plus the hostname shown in error text.
export interface ResmgrHostLike {
  id: string
  info?: { hostname?: string }
}

interface CallReason {
  err?: { message?: string; code?: string } | string
  message?: string
}

// Same message extraction as EditServerRolesModal's submitForm.
export const callErrorMessage = (reason: CallReason): string => {
  if (reason?.err && typeof reason.err !== 'string' && reason.err.code === 'ERR_BAD_RESPONSE') {
    return 'Something went wrong. Please try again.'
  }
  if (reason?.err && typeof reason.err !== 'string' && reason.err.message) {
    return reason.err.message
  }
  if (typeof reason?.err === 'string' && reason.err) {
    return reason.err
  }
  if (reason?.message) {
    return reason.message
  }
  return 'Unknown error'
}

const applyHostRoles = async (
  selection: EnrollmentSelection,
  host: ResmgrHostLike,
  currentConfigId: string | null | undefined,
): Promise<string | null> => {
  // Same as EditServerRolesModal: resmgr answers 409 when the host already has this config, so
  // skip it; a different config has to be removed before the new one can be set.
  try {
    if (currentConfigId !== selection.hostConfigId) {
      if (currentConfigId) {
        await unassignHostConfig({ hostId: host.id, configId: currentConfigId })
      }
      await assignHostConfig({ hostId: host.id, configId: selection.hostConfigId })
    }
  } catch (error) {
    return callErrorMessage(error as CallReason)
  }

  const roleCalls: Array<() => Promise<unknown>> = []
  if (selection.hypervisor) {
    roleCalls.push(() => assignHypervisorRole({ host, hostcluster: selection.clusterName }))
  }
  if (selection.imageLibrary) {
    roleCalls.push(() => assignImageLibraryRole({ host }))
  }
  if (selection.backends.length) {
    roleCalls.push(() =>
      assignPersistentStorageRole({ host, body: { backends: selection.backends } }),
    )
  }
  if (selection.remoteSupport) {
    roleCalls.push(() => updateRemoteSupportRole({ host }))
  }
  if (selection.dns) {
    roleCalls.push(() => assignDns({ host }))
  }

  for (const call of roleCalls) {
    try {
      await call()
    } catch (error) {
      return callErrorMessage(error as CallReason)
    }
  }
  return null
}

export const applyEnrollment = async (
  selection: EnrollmentSelection,
  hosts: ResmgrHostLike[],
): Promise<Record<string, string>> => {
  if (selection.hypervisor) {
    const { nova, neutron } = ApiClient.getInstance()
    try {
      await nova.checkHealth()
      await neutron.checkHealth()
    } catch {
      throw new ServicesNotReadyError()
    }
  }

  // Read each host's current config now, not from the list the drawer opened with: a retry
  // after a failed apply would otherwise assign a config the host already got (409).
  const liveHosts: Array<{ id: string; hostconfig_id?: string | null }> =
    await ApiClient.getInstance().resMgr.getHostsv2()
  const configOf = (id: string) => liveHosts.find((item) => item.id === id)?.hostconfig_id

  const errors: Record<string, string> = {}
  await Promise.all(
    hosts.map(async (host) => {
      const error = await applyHostRoles(selection, host, configOf(host.id))
      if (error) {
        errors[host.id] = error
      }
    }),
  )

  trackEvent('Enroll Hosts - Apply', { hosts: hosts.length, selection })
  return errors
}
